import {WebMercatorViewport} from '@deck.gl/core';
import {ScatterplotLayer, GeoJsonLayer, TextLayer} from '@deck.gl/layers';
import * as d3 from 'd3';
import * as PROPS from './props';
import { fitBounds } from './props';

import { Sun } from './sun.js';
import { timeseries } from './timeseries';

export const COORDINATE_ORIGIN = [19, 47];

const top7 = [
  "030100885/2018",
  "030100818/2015",
  "030100624/2017",
  "030100634/2015",
  "030101146/2018",
  "030101336/2016",
  "030100607/2016",
  "030100027/2015",
  "030101064/2016",
  "131300293/2016",
  "131300545/2017",
  "131300062/2015",
  "131300259/2015",
  "131300266/2017",
  "130800047/2015",
  "131300292/2015",
  "131300379/2017",
  "131200140/2015",
  "131200261/2018",
  "131200332/2018",
  "131200007/2016",
  "131200244/2015",
  "131200453/2017",
  "131200402/2018",
  "130200297/2017",
  "130200011/2015",
  "130200404/2015",
  "130200306/2018",
  "130200403/2018",
  "130200015/2017",
  "160100621/2016",
  "160100373/2017",
  "160100256/2018",
  "160100553/2015",
  "160100562/2015",
  "160100714/2017",
  "030100561/2015",
  "030101104/2018",
  "030100184/2015",
  "030100574/2017",
  "030101253/2017",
  "030100048/2018",
  "080101409/2016",
  "080101193/2016",
  "080101224/2017",
  "080100151/2017",
  "080100167/2016",
  "080100002/2018"
];

const top7Text = [
  { coordinates: [17.89242882306234, 47.586774115769884], name: '83' },
  { coordinates: [19.32200970060903, 47.639695710980405], name: 'M2' },
  { coordinates: [19.124523020353084, 47.14448700999251], name: '51' },
  { coordinates: [19.509324018705893, 46.92912206204559], name: '52' },
  { coordinates: [20.00262132148003, 46.91867042493929], name: '44' },
  { coordinates: [19.92821038663772, 47.27251193615761], name: '4' }
];

function getViewState(nw, se, props = {}) {
  return {
    transitionDuration: 2000,
    bearing: 0,
    pitch: 0,
    nw,
    se,
    ...fitBounds(window.innerWidth, window.innerHeight, nw, se),
    ...props
  }
}

function createViewport(viewState) {
  return new WebMercatorViewport(
    {
      width: window.innerWidth,
      height: window.innerHeight,
      ...viewState
    }
  )
}

function red(deck, data, _setText, _, text) {
  const viewState = getViewState(
    [16.0022982113, 45.5594811061],
    [24.510531447, 48.8238540716]
  );
  const fillColor = d => d.properties.inHun ? [250, 0, 0, 160] : [0, 0, 0, 0];
  const positionFn = d => d.geometry.coordinates;
  const layers = [
    new GeoJsonLayer({
      ...PROPS.GEO_JSON,
      getLineColor: [210, 210, 210, 255]
    }),
    new ScatterplotLayer({
      ...PROPS.SCATTERPLOT, data,
      getFillColor: fillColor,
      getPosition: positionFn,
      updateTriggers: {
        getFillColor: [fillColor],
        getPosition: [positionFn]
      },
      transitions: {
        getFillColor: {
          duration: 3000
        },
        getPosition: 1500
      },
      onHover: point => onHover(point, text)
    })
  ];
  deck.setProps({ viewState, layers });
}

const transformArt = ([_, x, y], width, height) => {
  const artPadding = (width + height) / 20;
  const artWidth = 1800 + artPadding + 70;
  const artHeight = 858 + artPadding;
  const size = 0.0013 * artPadding;
  return [(artWidth - x) * size,  window.innerHeight - (artHeight - y) * size];
}

const transformArtClosing = ([_, x, y], width, height) => {
  const artWidth = 1800 + 200;
  const artHeight = 858;
  const size = Math.min(width / artWidth, height / artHeight) * .60;
  return [(artWidth - x) * size,  height * .75 - (artHeight - y) * size];
}

// ==============================================
// Scenes
// ==============================================
const scenes = [
  // ==================
  // Opening scene - Sun
  // ==================
  (deck, data, setText, sunData, text) => {
    d3.select('#previousButton').style('visibility', 'hidden');
    const base = Math.min(window.innerWidth * 0.75, window.innerHeight) / 2;
    new Sun('content', 'sunContainer', sunData.all,
      text('months'), text('seasons'), text('weekdays'), {
      width: window.innerWidth * 0.75,
      height: window.innerHeight,
      innerRadius: base * 0.4,
      outerRadius: base * 0.8,
      transitionDuration: 1500
    });

    const viewState = getViewState(
      [16.0022982113, 45.5594811061],
      [24.510531447, 48.8238540716]
    );
    const viewport = createViewport(viewState);
    const fillColor = d => d.properties.art[0] === 0 ? [213, 0, 50] : [80, 80, 80];
    const positionFn = d => {
      const [lat, lng] = viewport.unproject(
        transformArt(d.properties.art, window.innerWidth, window.innerHeight));
      return [lat - 19, lng - 47];
    }
    const layers = [
      new GeoJsonLayer({
        ...PROPS.GEO_JSON,
        getLineColor: [210, 210, 210, 100]
      }),
      new ScatterplotLayer({
        ...PROPS.SCATTERPLOT, data,
        getFillColor: fillColor,
        getPosition: positionFn,
        updateTriggers: {
          getFillColor: [fillColor],
          getPosition: [positionFn]
        },
        transitions: {
          getPosition: 0
        }
      })
    ];
    setText('openingScene');
    deck.setProps({ viewState, layers });
  },

  // ============================
  // Sun + alcohol
  // ============================
  (deck, data, setText, sunData, text) => {
    d3.select('#previousButton').style('visibility', 'visible');
    d3.select('#timeseriesChart').remove();
    const base = Math.min(window.innerWidth * 0.75, window.innerHeight) / 2;
    new Sun('content', 'sunContainer',sunData.alcohol,
      text('months'), text('seasons'), text('weekdays'), {
      width: window.innerWidth * 0.75,
      height: window.innerHeight,
      innerRadius: base * 0.4,
      outerRadius: base * 0.8,
      transitionDuration: 1500
    });
    const viewState = getViewState(
      [16.0022982113, 45.5594811061],
      [24.510531447, 48.8238540716]
    );
    const viewport = createViewport(viewState);
    const fillColor = d => d.properties.art[0] === 0 ? [213, 0, 50] : [80, 80, 80];
    const positionFn = d => {
      const [lat, lng] = viewport.unproject(
        transformArt(d.properties.art, window.innerWidth, window.innerHeight));
      return [lat - 19, lng - 47];
    }
    const layers = [
      new GeoJsonLayer({
        ...PROPS.GEO_JSON,
        getLineColor: [210, 210, 210, 100]
      }),
      new ScatterplotLayer({
        ...PROPS.SCATTERPLOT, data,
        getFillColor: fillColor,
        getPosition: positionFn,
        updateTriggers: {
          getFillColor: [fillColor],
          getPosition: [positionFn]
        },
        transitions: {
          getPosition: 0
        }
      })
    ];
    setText('sunAlcoholScene');
    deck.setProps({ viewState, layers });
  },

  // ===========================
  // Timeseries scene 1
  // ===========================
  (deck, data, setText, _, text) => {
    d3.select('#sunContainer').remove();
    d3.select("#content").style("height","100vh");
    timeseries('content', 'timeseriesChart', {
      width: window.innerWidth * 0.7,
      height: window.innerHeight
    }, text);
    const viewState = getViewState(
      [16.0022982113, 45.5594811061],
      [24.510531447, 48.8238540716]
    );
    const viewport = createViewport(viewState);
    const fillColor = d => {
      if (!d.properties.inHun) return [0, 0, 0, 0];
      return d.properties.art[0] === 0 ? [213, 0, 50] : [80, 80, 80];
    }
    const positionFn = d => {
      const [lat, lng] = viewport.unproject(
        transformArt(d.properties.art, window.innerWidth, window.innerHeight));
      return [lat - 19, lng - 47];
    }
    const layers = [
      new GeoJsonLayer({
        ...PROPS.GEO_JSON,
        getLineColor: [210, 210, 210, 100]
      }),
      new ScatterplotLayer({
        ...PROPS.SCATTERPLOT, data,
        getFillColor: fillColor,
        getPosition: positionFn,
        updateTriggers: {
          getFillColor: [fillColor],
          getPosition: [positionFn]
        },
        transitions: {
          getPosition: 1500,
          getFillColor: 1500
        }
      })
    ];
    setText('timelineScene1');
    deck.setProps({ viewState, layers });
  },

  // ===========================
  // Timeseries scene 2
  // ===========================
  (deck, data, setText, _, text) => {
    d3.select('#mapHelp').style('display', 'none');
    d3.select('#sunContainer').remove();
    d3.select("#content").style("height","100vh");
    timeseries('content', 'timeseriesChart', {
      width: window.innerWidth * 0.7,
      height: window.innerHeight
    }, text, 'fatal');
    const viewState = getViewState(
      [16.0022982113, 45.5594811061],
      [24.510531447, 48.8238540716]
    );
    const viewport = createViewport(viewState);
    const fillColor = d => {
      if (!d.properties.inHun) return [0, 0, 0, 0];
      return d.properties.art[0] === 0 ? [213, 0, 50] : [80, 80, 80];
    }
    const positionFn = d => {
      const [lat, lng] = viewport.unproject(
        transformArt(d.properties.art, window.innerWidth, window.innerHeight));
      return [lat - 19, lng - 47];
    }
    const layers = [
      new GeoJsonLayer({
        ...PROPS.GEO_JSON,
        getLineColor: [210, 210, 210, 100]
      }),
      new ScatterplotLayer({
        ...PROPS.SCATTERPLOT, data,
        getFillColor: fillColor,
        getPosition: positionFn,
        updateTriggers: {
          getFillColor: [fillColor],
          getPosition: [positionFn]
        },
        transitions: {
          getPosition: 1500,
          getFillColor: 1500
        }
      })
    ];
    setText('timelineScene2');
    deck.setProps({ viewState, layers });
  },

  // ===================================
  // Explode car
  // ===================================
  (deck, data, setText, _, text) => {
    d3.select('#timeseriesChart').remove();
    d3.select("#content").style("height","auto");
    d3.select('#mapHelp').style('display', 'block');
    hideText();
    const viewState = getViewState(
      [16.0022982113, 45.5594811061],
      [24.510531447, 48.8238540716]
    );
    const fillColor = d => d.properties.inHun ? [100, 100, 100, 100] : [0, 0, 0, 0];
    const positionFn = d => d.geometry.coordinates;
    const layers = [
      new GeoJsonLayer({
        ...PROPS.GEO_JSON,
        getLineColor: [210, 210, 210, 255]
      }),
      new ScatterplotLayer({
        ...PROPS.SCATTERPLOT, data,
        getFillColor: fillColor,
        getPosition: positionFn,
        updateTriggers: {
          getFillColor: [fillColor],
          getPosition: [positionFn]
        },
        transitions: {
          getFillColor: {
            duration: 1500
          },
          getPosition: {
            duration: 1500,
            onEnd: () => red(deck, data, setText, null, text)
          }
        },
        onHover: point => onHover(point, text)
      })
    ];
    setText('redScene');
    deck.setProps({ viewState, layers });
  },

  // =========================
  // Budapest scene
  // =========================
  (deck, data, setText, _, text) => {
    const viewState = getViewState(
      [18.847517657125317, 47.68082628490495] ,
      [19.438136065480165, 47.40057019780332],
      {
        pitch: 49.83870967741936,
        transitionDuration: 3000
      }
    );
    const fillColor = d => d.properties.inHun ? [250, 0, 0, 160] : [0, 0, 0, 0];
    const positionFn = d => d.geometry.coordinates;
    const layers = [
      new GeoJsonLayer({
        ...PROPS.GEO_JSON,
        getLineColor: [210, 210, 210, 255]
      }),
      new ScatterplotLayer({
        ...PROPS.SCATTERPLOT, data,
        getFillColor: fillColor,
        getPosition: positionFn,
        updateTriggers: {
          getFillColor: [fillColor],
          getPosition: [positionFn]
        },
        transitions: {
          getFillColor: 1500,
          getPosition: 1500
        },
        onHover: point => onHover(point, text)
      })
    ];
    setText('budapestScene'),
    deck.setProps({ viewState, layers });
  },


  // ==================================
  // top 7
  // ==================================
  (deck, data, setText, _, text) => {
    d3.select('#info').style('visibility', 'hidden');
    d3.select('#mapHelp').style('display', 'block');
    d3.select('#forwardButton').classed('lastScene', false).text(text('forward'));
    d3.select("#content").style("height","auto");
    hideLabels();
    const viewState = getViewState(
      [17.571970036608526, 48.38604592591604],
      [21.496236280597465, 46.13824249237775],
      {
        pitch: 40
      }
    );
    const fillColor = d => {
      if (!d.properties.inHun) return [0, 0, 0, 0];
      return top7.indexOf(d.id) > -1 ? [0, 0, 250, 100] : [250, 0, 0, 50];
    }
    const radius = d => top7.indexOf(d.id) > -1 ? 200 : 10;
    const positionFn = d => d.geometry.coordinates;
    const layers = [
      new GeoJsonLayer({
        ...PROPS.GEO_JSON,
        getLineColor: [210, 210, 210, 255]
      }),
      new ScatterplotLayer({
        ...PROPS.SCATTERPLOT
        , data,
        getFillColor: fillColor,
        getPosition: positionFn,
        getRadius: radius,
        updateTriggers: {
          getFillColor: [fillColor],
          getPosition: [positionFn],
          getRadius: [radius]
        },
        transitions: {
          getFillColor: 1500,
          getPosition: 1500,
          getRadius: 1500
        },
        onHover: point => onHover(point, text)
      }),
      new TextLayer({
        id: 'text-layer',
        data: top7Text,
        pickable: true,
        getPosition: d => d.coordinates,
        getText: d => text('road' + d.name),
        getColor: [0, 0, 0, 200],
        getSize: 10000,
        getAngle: 0,
        getTextAnchor: 'left',
        getAlignmentBaseline: 'bottom',
        sizeUnits: 'meters',
        fontWeight: 'bold',
        fontFamily: 'sans-serif',
        characterSet: 'főútM0123456789.aes-'.split('')
      })
    ];
    setText('top7Scene'),
    deck.setProps({ viewState, layers });
  },
  // ====================
  // Closing scene
  // ====================
  (deck, data, setText, _, text) => {
    d3.select('#info').style('visibility', 'visible');
    d3.select('#mapHelp').style('display', 'none');
    d3.select('#forwardButton').classed('lastScene', true).text(text('goToMap'));
    d3.select("#content").style("height","100vh");
    showLabels(data, text);
    const viewState = getViewState(
      [18.60553840465445, 47.55409865896078],
      [20.053543119103129, 45.95150973090965],
      {
        pitch: 0
      }
    );
    const fillColor = [160, 160, 160, 100];
    const viewport = createViewport(viewState);
    const positionFn = d => {
      const [lat, lng] = viewport.unproject(
        transformArtClosing(d.properties.art, window.innerWidth, window.innerHeight));
      return [lat - 19, lng - 47];
    }
    const layers = [
      new GeoJsonLayer({
        ...PROPS.GEO_JSON,
        getLineColor: [210, 210, 210, 0]
      }),
      new ScatterplotLayer({
        ...PROPS.SCATTERPLOT, data,
        getFillColor: fillColor,
        getPosition: positionFn,
        updateTriggers: {
          getFillColor: [fillColor],
          getPosition: [positionFn]
        },
        transitions: {
          getFillColor: 1500,
          getPosition: 1500
        }
      })
    ];
    setText('closingScene'),
    deck.setProps({ viewState, layers });
  }
];


// ==================================
// show labels
// ==================================

const formatDate = (date, text) => {
  const [year, month] = date.split('-');
  return `${year}.&nbsp;${text('MONTH_' + month)}`;
}

const labelText = (d, text) => `
  <table class="tooltipText">
    <tr>
      <th></th><th>${text('okozo')}:</th><th>${text('kimenetel')}:</th>
    </tr>
    <tr>
      <td>${formatDate(d.properties.date, text)}</td>
      <td>${text('VEHICLE_' + d.properties.vehicle)}</td>
      <td>${text('SEVERITY_' + d.properties.severity30)}</td>
    </tr>
  </table>`;

let intervalref;
function showLabels(data, text) {
  d3.select('#label').style("display","block");
  d3.select('#labelPoint').style("display","block");

  clearInterval(intervalref);
  intervalref = setInterval(() => {
    const d = data[Math.floor(Math.random() * data.length)];
    const [x, y] = transformArtClosing(d.properties.art, window.innerWidth, window.innerHeight);
    const label = d3.select('#label');

    label.transition()
      .duration(1500)
      .style('opacity', 0)
      .on('end', () => {
        label.html(labelText(d, text));
        label.style(x > window.innerWidth * .3 ? 'right' : 'left', (x > window.innerWidth * .3 ?  window.innerWidth - x - 30: x + 20) + 'px')
          .style(x > window.innerWidth * .3 ? 'left' : 'right', null)
          .style('top', y + 20 + 'px');
        label.transition()
          .duration(1500)
          .style('opacity', 1);
      });

    // label red dot
    d3.select("#labelPoint")
      .transition()
      .duration(1500)
        .style('opacity', 0)
      .transition()
      .duration(0)
        .style('left', x + 'px')
        .style('top', y + 'px')
      .transition()
      .duration(1500)
        .style('opacity', 1);
  }, 5000);
}

// ==================================
// hide labels
// ==================================
function hideLabels() {
  clearInterval(intervalref);
  d3.select("#labelPoint")
    .style('left', '-1000px')
    .style("display","none");
  d3.select('#label')
    .style('left', '-1000px')
    .style('right', null)
    .style("display","none");
}

// ===================================
// exported & aux function
// ===================================

function onHover(point, text) {
  if (point.object) {
    tooltip(point.x, point.y, labelText(point.object, text));
  } else {
    d3.select('#tooltip')
      .style('left', '-1000px')
      .style('right', null)
      .classed('wider', false)
  }
}

function tooltip(x, y, text) {
  d3.select('#tooltip')
    .style(x > window.innerWidth / 2 ? 'right' : 'left', (x > window.innerWidth / 2 ? window.innerWidth - x : x) + 20 + 'px')
    .style(x > window.innerWidth / 2 ? 'left' : 'right', null)
    .style('top', y + 20 + 'px')
    .classed('wider', true)
    .html(text);
}

function hideText() {
  d3.select('#text')
    .transition()
      .duration(500)
      .style('opacity', 0);
}

export default (deck, data, sunData, text) => {
  const setText = key => {
    d3.select('#text')
      .attr('class', key)
        .style('opacity', 0)
        .html(text(key))
      .transition()
        .duration(1000)
        .style('opacity', 1);
  };
  return [
    scene => scenes[scene](deck, data, setText, sunData, text),
    scenes.length
  ];
}